import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PortfolioPage = ({ trades, wallets }) => {
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [tokenPrices, setTokenPrices] = useState({});

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Fetch token price from DexScreener
  const fetchTokenPrice = async (tokenAddress) => {
    try {
      const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
      if (response.data && response.data.pairs && response.data.pairs.length > 0) {
        const pair = response.data.pairs[0];
        return parseFloat(pair.priceUsd) || 0;
      }
      return 0;
    } catch (error) {
      console.error(`Failed to fetch price for token ${tokenAddress}:`, error);
      return 0;
    }
  };

  // Fetch prices for all active trades
  const fetchTokenPrices = async () => {
    const activeTrades = trades.filter(trade => 
      hasBuyTransactionIcon(trade) && !trade.sell_time && trade.token_address
    );
    
    const pricePromises = activeTrades.map(async (trade) => {
      const price = await fetchTokenPrice(trade.token_address);
      return { tokenAddress: trade.token_address, price };
    });
    
    try {
      const prices = await Promise.all(pricePromises);
      const priceMap = {};
      prices.forEach(({ tokenAddress, price }) => {
        priceMap[tokenAddress] = price;
      });
      setTokenPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch token prices:', error);
    }
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Helper function to calculate P/L with fallback to current price calculation
  const calculateProfitLoss = (trade) => {
    let profitLoss = trade.profit_loss;
    
    // For completed trades (with sell_time), always use the stored profit_loss
    if (trade.sell_time) {
      return profitLoss || 0;
    }
    
    // For active trades only: If profit_loss is 0, null, or undefined, calculate it from current price vs buy price
    if ((profitLoss === 0 || profitLoss === null || profitLoss === undefined) && 
        trade.buy_price && trade.buy_price > 0) {
      
      // Try to get real-time price from tokenPrices state
      const realTimePrice = tokenPrices[trade.token_address];
      
      // Use real-time price if available, otherwise use current_price, then buy_price as fallback
      const currentPrice = realTimePrice || trade.current_price || trade.buy_price;
      
      // Calculate P/L if we have a different price than buy price
      if (currentPrice !== trade.buy_price && currentPrice > 0) {
        profitLoss = ((currentPrice - trade.buy_price) / trade.buy_price) * 100;
      } else {
        // If prices are the same or invalid, P/L is 0
        profitLoss = 0;
      }
    }
    
    return profitLoss || 0;
  };

  // Helper function for holding percentage
  const getHoldingPercentage = (trade) => {
    // If no buy transaction, return 0
    if (!trade.buy_time) return 0;
    
    // For tokens with buy transactions, default to 100% if amounts are not properly set
    if (!trade.original_amount || trade.original_amount <= 0) {
      // If we have a buy transaction but no original_amount, assume 100% holding
      return 100;
    }
    
    const currentAmount = trade.amount || trade.original_amount || 0;
    return Math.round((currentAmount / trade.original_amount) * 100);
  };

  // Calculate trade statistics
  const allCount = trades.length;
  const activeCount = trades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
  const completedCount = trades.filter(trade => hasBuyTransactionIcon(trade) && (getHoldingPercentage(trade) === 0 || trade.sell_time)).length;
  const pendingCount = trades.filter(trade => trade.status === 'pending').length;
  const nothingCount = trades.filter(trade => !hasBuyTransactionIcon(trade)).length;
  const failedCount = trades.filter(trade => 
    (trade.status === 'failed' || trade.failed) || 
    (trade.buy_time && !hasBuyTransactionIcon(trade)) || // Buy attempted but no transaction ID
    (trade.transaction_warning && trade.transaction_warning.includes('failed'))
  ).length;

  // Calculate profit/loss statistics
  const completedTrades = trades.filter(trade => trade.sell_time);
  const profitableTrades = completedTrades.filter(trade => calculateProfitLoss(trade) > 0);
  const lossTrades = completedTrades.filter(trade => calculateProfitLoss(trade) < 0);
  const winRate = completedTrades.length > 0 ? (profitableTrades.length / completedTrades.length * 100) : 0;

  const totalProfitLoss = completedTrades.reduce((sum, trade) => {
    return sum + calculateProfitLoss(trade);
  }, 0);

  const totalProfit = profitableTrades.reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);
  const totalLoss = lossTrades.reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);

  // Calculate wallet portfolio
  const formatUsdValue = (balance) => {
    if (!solPrice || !balance) return '$0.00';
    const usdValue = balance * solPrice;
    return `$${usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTotalPortfolioValue = () => {
    if (!solPrice) return 0;
    return wallets.reduce((total, wallet) => {
      return total + (wallet.balance * solPrice);
    }, 0);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSolPrice(); // Fetch SOL price
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchSolPrice(); // Update SOL price every 30 seconds
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch token prices when trades change
  useEffect(() => {
    if (trades.length > 0) {
      fetchTokenPrices();
    }
  }, [trades]);

  return (
    <div className="space-y-6">
      {/* Portfolio Overview */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h2 className="text-2xl font-bold text-white mb-6">Portfolio Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Portfolio Value</div>
            <div className="text-2xl font-bold text-green-400">
              ${getTotalPortfolioValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {wallets.length} wallet{wallets.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">SOL Price</div>
            <div className="text-2xl font-bold text-blue-400">
              {loadingSolPrice ? 'Loading...' : `$${solPrice?.toFixed(2) || 'N/A'}`}
            </div>
            <div className="text-xs text-slate-500 mt-1">Current Market Price</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total SOL</div>
            <div className="text-2xl font-bold text-purple-400">
              {wallets.reduce((sum, wallet) => sum + (parseFloat(wallet.balance) || 0), 0).toFixed(4)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Combined Balance</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Active Wallets</div>
            <div className="text-2xl font-bold text-yellow-400">
              {wallets.filter(w => (w.status || 'active') === 'active').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Connected & Active</div>
          </div>
        </div>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Wallet Portfolio Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            💰 Wallet Portfolio
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Total Wallets:</span>
              <span className="text-white font-semibold">{wallets.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">SOL Price:</span>
              <span className="text-green-400 font-semibold">
                {loadingSolPrice ? 'Loading...' : `$${solPrice?.toFixed(2) || 'N/A'}`}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Total SOL:</span>
              <span className="text-blue-400 font-semibold">
                {wallets.reduce((sum, wallet) => sum + wallet.balance, 0).toFixed(4)} SOL
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-600">
              <span className="text-white font-semibold">Portfolio Value:</span>
              <span className="text-green-400 font-bold text-lg">
                ${getTotalPortfolioValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Trading Statistics Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            📊 Trading Statistics
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Win Rate:</span>
              <span className={`font-semibold ${winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                {winRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Profitable Trades:</span>
              <span className="text-green-400 font-semibold">{profitableTrades.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Loss Trades:</span>
              <span className="text-red-400 font-semibold">{lossTrades.length}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-600">
              <span className="text-white font-semibold">Net P&L:</span>
              <span className={`font-bold text-lg ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {/* Trade Counts Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            📈 Trade Overview
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">All Trades:</span>
              <span className="text-blue-400 font-semibold">{allCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Active:</span>
              <span className="text-purple-400 font-semibold">{activeCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Completed:</span>
              <span className="text-green-400 font-semibold">{completedCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Pending:</span>
              <span className="text-yellow-400 font-semibold">{pendingCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Failed:</span>
              <span className="text-red-400 font-semibold">{failedCount}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Detailed Portfolio Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Active Trades */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-xl font-semibold text-white mb-4">Active Trades</h3>
          {activeCount === 0 ? (
            <p className="text-slate-400 text-center py-8">No active trades</p>
          ) : (
            <div className="space-y-3">
              {trades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).slice(0, 5).map((trade, index) => (
                <div key={index} className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 font-medium">
                      {trade.symbol || trade.name || 'Unknown Token'}
                    </span>
                    <span className="text-purple-400 text-xs bg-purple-900/30 px-2 py-1 rounded">
                      ACTIVE
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-white font-semibold">{trade.amount || 0} SOL</div>
                      <div className="text-slate-400 text-sm">
                        {loadingSolPrice ? 'Loading...' : solPrice ? `$${((trade.amount || 0) * solPrice).toFixed(2)}` : 'N/A'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-400 text-xs">P&L</div>
                      <div className={`text-sm font-semibold ${calculateProfitLoss(trade) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {calculateProfitLoss(trade) >= 0 ? '+' : ''}{calculateProfitLoss(trade).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {activeCount > 5 && (
                <p className="text-slate-400 text-center text-sm">
                  ... and {activeCount - 5} more active trades
                </p>
              )}
            </div>
          )}
        </div>

        {/* Profit/Loss Breakdown */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-xl font-semibold text-white mb-4">P&L Breakdown</h3>
          <div className="space-y-4">
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-green-400 font-semibold">Total Profits</span>
                <span className="text-green-400 font-bold text-lg">+{totalProfit.toFixed(2)}%</span>
              </div>
              <div className="text-green-300 text-sm mt-1">
                From {profitableTrades.length} profitable trades
              </div>
            </div>
            
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-red-400 font-semibold">Total Losses</span>
                <span className="text-red-400 font-bold text-lg">{totalLoss.toFixed(2)}%</span>
              </div>
              <div className="text-red-300 text-sm mt-1">
                From {lossTrades.length} loss trades
              </div>
            </div>
            
            <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-white font-semibold">Net Result</span>
                <span className={`font-bold text-xl ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
                </span>
              </div>
              <div className="text-slate-400 text-sm mt-1">
                Overall trading performance
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Wallet Details */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-xl font-semibold text-white mb-4">Wallet Details</h3>
        {wallets.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No wallets connected</p>
        ) : (
          <div className="space-y-3">
            {wallets.map((wallet, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400 font-mono text-sm">
                    {wallet.public_key.slice(0, 6)}...{wallet.public_key.slice(-6)}
                  </span>
                  <span className="text-green-400 text-xs bg-green-900/30 px-2 py-1 rounded">
                    {wallet.status || 'Active'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-semibold">{wallet.balance} SOL</div>
                    <div className="text-slate-400 text-sm">{formatUsdValue(wallet.balance)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400 text-xs">Last Updated</div>
                    <div className="text-slate-300 text-xs">
                      {wallet.last_updated ? new Date(wallet.last_updated).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trading Strategy Analysis */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-xl font-semibold text-white mb-6">Trading Strategy Analysis</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">{winRate.toFixed(1)}%</div>
            <div className="text-slate-400 text-sm">Win Rate</div>
            <div className="text-xs text-slate-500 mt-1">
              {winRate >= 70 ? 'Excellent' : winRate >= 50 ? 'Good' : 'Needs Improvement'}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-3xl font-bold mb-2 ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
            </div>
            <div className="text-slate-400 text-sm">Net P&L</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalProfitLoss >= 0 ? 'Profitable Strategy' : 'Loss-Making Strategy'}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">{allCount}</div>
            <div className="text-slate-400 text-sm">Total Trades</div>
            <div className="text-xs text-slate-500 mt-1">
              {allCount >= 50 ? 'Good Sample Size' : allCount >= 20 ? 'Moderate Sample' : 'Small Sample'}
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-slate-900/50 border border-slate-600 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-3">Strategy Recommendations</h4>
          <div className="space-y-2">
            {winRate >= 70 && totalProfitLoss >= 0 ? (
              <p className="text-green-400 text-sm">
                ✅ Your strategy shows excellent performance. Consider increasing position sizes gradually.
              </p>
            ) : winRate >= 50 && totalProfitLoss >= 0 ? (
              <p className="text-yellow-400 text-sm">
                ⚠️ Your strategy shows moderate performance. Consider refining your entry/exit criteria.
              </p>
            ) : (
              <p className="text-red-400 text-sm">
                ❌ Your strategy needs improvement. Analyze failed trades and adjust parameters.
              </p>
            )}
            
            <p className="text-blue-400 text-sm">
              🔄 Continue trading to gather more data and improve your strategy.
            </p>
            
            <p className="text-slate-400 text-sm">
              📊 Monitor your portfolio performance regularly to track improvements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;
